import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApiRequest from "../../lib/hooks/useRequest";
import {
  IPatientRequestListEntities,
  IPatientRequestListItem,
  IPatientRequestListPaginated,
  IPatientRequestPublicLocation,
  IPatientRequestStatusOption,
} from "../../lib/interfaces/patient-request";
import AppStatus from "../applicants/AppStatus";
import { PatientRequestEmbedCode } from "./PatientRequestEmbedCode";

const SEITENGROESSE = 20;

/**
 * Die Anfrage-Liste im Zuschnitt der Bewerberliste (Prototyp-Variante A, Gate
 * prototypPasst am 21.10.2026): Filterzeile oben, Tabelle darunter, Status als Plakette.
 *
 * Filter, Suche und Seitenaufteilung laufen serverseitig — die Liste holt pro Zustand
 * frisch (Issue KAN-228).
 */
export const PatientRequestsTable = () => {
  const { apiRequest } = useApiRequest();
  const navigate = useNavigate();

  const [anfragen, setAnfragen] = useState<IPatientRequestListItem[]>([]);
  const [statusOptionen, setStatusOptionen] = useState<IPatientRequestStatusOption[]>([]);
  const [standorte, setStandorte] = useState<IPatientRequestPublicLocation[]>([]);
  const [gesamt, setGesamt] = useState(0);
  const [seite, setSeite] = useState(1);
  const [laedt, setLaedt] = useState(true);

  const [suche, setSuche] = useState("");
  const [standortFilter, setStandortFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  // Der neueste Eingang steht oben — das ist die Reihenfolge, in der ein Betrieb seine
  // Anfragen abarbeitet. Ein Klick auf die Spalte dreht sie um.
  const [sortierung, setSortierung] = useState<"asc" | "desc">("desc");

  const farbeFuer = useCallback(
    (statusId: string): IPatientRequestStatusOption | undefined => statusOptionen.find((option) => option.id === statusId),
    [statusOptionen]
  );

  const abfrage = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(seite));
    params.set("limit", String(SEITENGROESSE));
    if (standortFilter) params.set("belongsToCompanyLocationId", standortFilter);
    if (statusFilter) params.set("belongsToStatus", statusFilter);
    // Der Server verlangt mindestens drei Zeichen, sonst sucht er nicht.
    if (suche.trim().length >= 3) params.set("search", suche.trim());
    params.set("sortReceivedAt", sortierung);
    return params.toString();
  }, [seite, standortFilter, statusFilter, suche, sortierung]);

  useEffect(() => {
    const laden = async () => {
      setLaedt(true);
      const antwort = await apiRequest<IPatientRequestListPaginated>(`patient-requests?${abfrage}`, "GET");
      if (antwort.data) {
        setAnfragen(antwort.data.docs ?? []);
        setGesamt(antwort.data.count ?? 0);
      }
      setLaedt(false);
    };
    void laden();
  }, [apiRequest, abfrage]);

  useEffect(() => {
    const ladenEntities = async () => {
      const entities = await apiRequest<IPatientRequestListEntities>("patient-requests/list-entities", "GET");
      if (entities.data?.status) setStatusOptionen(entities.data.status);
      // Nur die Haeuser, die dieser Nutzer sehen darf — der Server entscheidet das.
      if (entities.data?.companyLocations) setStandorte(entities.data.companyLocations);
    };
    void ladenEntities();
  }, [apiRequest]);

  const seitenAnzahl = Math.max(1, Math.ceil(gesamt / SEITENGROESSE));

  if (!laedt && gesamt === 0 && !suche && !standortFilter && !statusFilter) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
        <h3 className="mb-2 text-lg font-medium">Noch keine Patientenanfragen</h3>
        <p className="mx-auto max-w-xl text-sm text-gray-500">
          Sobald jemand das Anfrageformular auf Ihrer Website absendet, erscheint die Anfrage hier.
        </p>
        {/* Derselbe Weg wie in der Kopfzeile: Wer hier steht, hat das Formular meist noch
            nicht eingebunden — der Einstieg gehoert deshalb genau hierhin (Issue KAN-233). */}
        <div className="mt-4">
          <PatientRequestEmbedCode />
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 p-3">
        <input
          className="h-10 min-w-[180px] flex-1 rounded-md border border-gray-300 px-3 text-sm"
          placeholder="Nach Name suchen"
          value={suche}
          onChange={(event) => {
            setSuche(event.target.value);
            setSeite(1);
          }}
        />
        <select
          className="h-10 rounded-md border border-gray-300 px-3 text-sm"
          value={standortFilter}
          onChange={(event) => {
            setStandortFilter(event.target.value);
            setSeite(1);
          }}
        >
          <option value="">Alle Standorte</option>
          {standorte.map((standort) => (
            <option key={standort._id} value={standort._id}>
              {standort.title}
            </option>
          ))}
        </select>
        <select
          className="h-10 rounded-md border border-gray-300 px-3 text-sm"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setSeite(1);
          }}
        >
          <option value="">Alle Status</option>
          {statusOptionen.map((option) => (
            <option key={option.id} value={option.id}>
              {option.value}
            </option>
          ))}
        </select>

        {/* Bewusst in der Kopfzeile der Liste statt in einer eigenen Zeile darueber: Oben
            auf dieser Seite kommt spaeter die Auswertungskarte dazu (Issue KAN-232). */}
        <div className="ml-auto">
          <PatientRequestEmbedCode />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              {["Name", "Standort", "Eingegangen", "Status", "Kontakt"].map((spalte) => (
                <th
                  key={spalte}
                  className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                >
                  {spalte === "Eingegangen" ? (
                    <button
                      type="button"
                      className="flex items-center gap-1 uppercase tracking-wide hover:text-gray-700"
                      onClick={() => {
                        setSortierung((bisher) => (bisher === "desc" ? "asc" : "desc"));
                        setSeite(1);
                      }}
                      aria-label={
                        sortierung === "desc"
                          ? "Nach Eingang sortieren, neueste zuerst. Klicken für älteste zuerst."
                          : "Nach Eingang sortieren, älteste zuerst. Klicken für neueste zuerst."
                      }
                    >
                      {spalte}
                      <span aria-hidden="true">{sortierung === "desc" ? "↓" : "↑"}</span>
                    </button>
                  ) : (
                    spalte
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {laedt && (
              <tr>
                <td className="px-4 py-6 text-sm text-gray-500" colSpan={5}>
                  Anfragen werden geladen…
                </td>
              </tr>
            )}
            {!laedt && anfragen.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-sm text-gray-500" colSpan={5}>
                  Keine Anfrage passt zu dieser Auswahl.
                </td>
              </tr>
            )}
            {!laedt &&
              anfragen.map((anfrage) => {
                const status = farbeFuer(anfrage.belongsToStatus);
                return (
                  <tr
                    key={anfrage._id}
                    className="cursor-pointer border-b border-gray-100 hover:bg-gray-50"
                    onClick={() => navigate(`/patientenanfragen/${anfrage._id}`)}
                  >
                    <td className="px-4 py-3 text-sm font-medium">
                      {[anfrage.firstName, anfrage.lastName].filter(Boolean).join(" ")}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{anfrage.companyLocationTitle ?? "—"}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(anfrage.receivedAt).toLocaleString("de-DE", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="w-fit">
                        <AppStatus status={status?.value ?? anfrage.belongsToStatus} color={status?.color ?? "#6b7280"} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{anfrage.phone || anfrage.mail || "—"}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 text-sm text-gray-500">
        <span>
          {gesamt} {gesamt === 1 ? "Anfrage" : "Anfragen"}
        </span>
        <div className="flex items-center gap-2">
          <button
            className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40"
            disabled={seite <= 1}
            onClick={() => setSeite((aktuell) => Math.max(1, aktuell - 1))}
          >
            Zurück
          </button>
          <span>
            Seite {seite} von {seitenAnzahl}
          </span>
          <button
            className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-40"
            disabled={seite >= seitenAnzahl}
            onClick={() => setSeite((aktuell) => aktuell + 1)}
          >
            Weiter
          </button>
        </div>
      </div>
    </div>
  );
};
